import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  FiTruck, FiArrowRight, FiAlertCircle, FiX
} from "react-icons/fi";
import { MdDelete } from "react-icons/md";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Slide } from 'react-toastify';
import Swal from 'sweetalert2';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import LeftMenu from '../components/LeftMenu';
import withAuth from '../common/auth';
import axios from 'axios';
import { GET_MASTER_DATA, BASE_URL, GET_ITEM_MASTER, GET_PRODUCT_BATCHES, POST_STOCK_TRANSFER } from '../utlilities/config';

// Enhanced Spinner Component
const Spinner = () => (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
      <div className="flex flex-col items-center gap-3">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
        <p className="text-indigo-600 font-medium">Loading...</p>
      </div>
    </div>
);

// Batch Selection Modal Component (aligned with SalesEntry: table layout, expiry styling, near-expiry confirm)
const batchAvailableQty = (batch) =>
  parseFloat(batch?.availableQty ?? batch?.currentStock ?? 0);

const BatchSelectionModal = ({
  isOpen,
  onClose,
  product,
  batches,
  onSelectBatch,
  generalsettings,
  onReopen,
}) => {
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const modalRef = useRef(null);
  const selectButtonRef = useRef(null);

  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    try {
      const expiry = new Date(expiryDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      expiry.setHours(0, 0, 0, 0);
      return expiry < today;
    } catch {
      return false;
    }
  };

  const isExpiringSoon = (expiryDate) => {
    if (!expiryDate || !generalsettings?.phExpiryDays) return false;
    try {
      const expiry = new Date(expiryDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      expiry.setHours(0, 0, 0, 0);

      if (expiry < today) return false;

      const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
      const expiryDays = parseInt(generalsettings.phExpiryDays) || 0;

      return daysUntilExpiry <= expiryDays;
    } catch {
      return false;
    }
  };

  const formatExpiryDate = (expiryDate) => {
    if (!expiryDate) return 'N/A';
    try {
      const date = new Date(expiryDate);
      if (isNaN(date.getTime())) return expiryDate;

      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = String(date.getFullYear()).slice(-2);
      return `${month}/${year}`;
    } catch {
      return expiryDate;
    }
  };

  // Hide zero-qty batches from the select list
  const availableBatches = useMemo(
    () => (Array.isArray(batches) ? batches.filter((batch) => batchAvailableQty(batch) > 0) : []),
    [batches]
  );

  useEffect(() => {
    if (isOpen && availableBatches.length > 0) {
      const firstValidBatch = availableBatches.findIndex((batch) => !isExpired(batch.expiryDate));
      if (firstValidBatch !== -1) {
        setSelectedBatch(availableBatches[firstValidBatch]);
        setSelectedIndex(firstValidBatch);
      } else {
        setSelectedBatch(availableBatches[0]);
        setSelectedIndex(0);
      }
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (modalRef.current) {
            if (document.activeElement && document.activeElement.tagName === 'INPUT') {
              document.activeElement.blur();
            }
            modalRef.current.focus();
          }
        }, 250);
      });
    } else if (isOpen) {
      setSelectedBatch(null);
      setSelectedIndex(0);
    }
  }, [isOpen, availableBatches]);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowUp' && selectedIndex > 0) {
      e.preventDefault();
      const newIndex = selectedIndex - 1;
      let validIndex = newIndex;
      while (validIndex >= 0 && isExpired(availableBatches[validIndex].expiryDate)) {
        validIndex--;
      }
      if (validIndex >= 0) {
        setSelectedIndex(validIndex);
        setSelectedBatch(availableBatches[validIndex]);
      }
    } else if (e.key === 'ArrowDown' && selectedIndex < availableBatches.length - 1) {
      e.preventDefault();
      const newIndex = selectedIndex + 1;
      let validIndex = newIndex;
      while (validIndex < availableBatches.length && isExpired(availableBatches[validIndex].expiryDate)) {
        validIndex++;
      }
      if (validIndex < availableBatches.length) {
        setSelectedIndex(validIndex);
        setSelectedBatch(availableBatches[validIndex]);
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      handleSelectBatch();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  const handleSelectBatch = async () => {
    if (!selectedBatch) {
      toast.error('Please select a batch', { autoClose: 1000 });
      return;
    }

    if (isExpired(selectedBatch.expiryDate)) {
      toast.error('Cannot select expired batch', { autoClose: 2000 });
      return;
    }

    if (isExpiringSoon(selectedBatch.expiryDate)) {
      onClose();

      await new Promise((resolve) => setTimeout(resolve, 300));

      const formattedExpiry = formatExpiryDate(selectedBatch.expiryDate);
      const result = await Swal.fire({
        title: 'Expiry Warning',
        html: `This batch is expiring soon.<br/>Expiry Date: <strong>${formattedExpiry}</strong><br/><br/>Do you want to continue?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#4f46e5',
        cancelButtonColor: '#6b7280',
        confirmButtonText: 'Yes, Continue',
        cancelButtonText: 'Cancel',
        customClass: {
          confirmButton: 'rounded-lg text-sm font-medium px-5 py-2',
          cancelButton: 'rounded-lg text-sm font-medium px-5 py-2 mr-2',
          popup: 'rounded-xl shadow-xl',
        },
      });

      if (!result.isConfirmed) {
        if (onReopen) {
          onReopen();
        }
        return;
      }

      await new Promise((resolve) => setTimeout(resolve, 100));
      onSelectBatch(selectedBatch);
      return;
    }

    onSelectBatch(selectedBatch);
    onClose();
  };

  if (!isOpen) return null;

  const productLabel =
    product?.label || product?.Item_Name || product?.ItemName || product?.name || 'Product';

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black bg-opacity-50">
      <div
        className="bg-white rounded-lg shadow-xl w-[90vw] max-w-3xl max-h-[80vh] overflow-hidden outline-none flex flex-col"
        ref={modalRef}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
      >
        <div className="bg-indigo-600 text-white px-6 py-2 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold">Select Batch</h3>
            <p className="text-sm text-indigo-100 mt-1">{productLabel}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 hover:bg-indigo-700 rounded transition-colors"
          >
            <FiX size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <table className="w-full border-collapse">
            <thead className="bg-gray-100 sticky top-0">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase border-b">
                  Batch No
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase border-b">
                  Expiry
                </th>
                <th className="px-4 py-2 text-right text-xs font-semibold text-gray-700 uppercase border-b">
                  MRP
                </th>
                <th className="px-4 py-2 text-right text-xs font-semibold text-gray-700 uppercase border-b">
                  Available Qty
                </th>
              </tr>
            </thead>
            <tbody>
              {availableBatches.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-500">
                    No batches with available quantity
                  </td>
                </tr>
              ) : (
                availableBatches.map((batch, index) => {
                const isSelected = selectedIndex === index;
                const expired = isExpired(batch.expiryDate);
                const expiringSoon = isExpiringSoon(batch.expiryDate);
                const qty = batchAvailableQty(batch);

                return (
                  <tr
                    key={index}
                    className={`transition-colors ${
                      expired
                        ? 'bg-red-50 border-l-4 border-red-500 cursor-not-allowed opacity-60'
                        : expiringSoon
                          ? isSelected
                            ? 'bg-amber-50 border-l-4 border-amber-500 cursor-pointer'
                            : 'bg-amber-50/30 border-l-4 border-amber-300 cursor-pointer hover:bg-amber-50'
                          : isSelected
                            ? 'bg-indigo-50 border-l-4 border-indigo-500 cursor-pointer'
                            : 'hover:bg-gray-50 border-l-4 border-transparent cursor-pointer'
                    }`}
                    onClick={() => {
                      if (!expired) {
                        setSelectedIndex(index);
                        setSelectedBatch(batch);
                      }
                    }}
                  >
                    <td className="px-4 py-2 text-sm font-medium border-b">
                      <span className={expired ? 'text-red-700 line-through' : 'text-gray-900'}>
                        {batch.batchNo || 'N/A'}
                      </span>
                      {expired && (
                        <span className="ml-2 text-xs text-red-600 font-semibold">(EXPIRED)</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-sm border-b">
                      <span
                        className={
                          expired
                            ? 'text-red-700 font-semibold'
                            : expiringSoon
                              ? 'text-amber-700 font-semibold'
                              : 'text-gray-700'
                        }
                      >
                        {formatExpiryDate(batch.expiryDate)}
                      </span>
                      {expiringSoon && !expired && (
                        <span className="ml-2 text-xs text-amber-600 font-semibold">(Near Expiry)</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-sm font-semibold text-right border-b">
                      <span className={expired ? 'text-red-700' : 'text-gray-900'}>
                        ₹{parseFloat(batch.mrp || 0).toFixed(2)}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-sm font-semibold text-right border-b">
                      <span
                        className={`px-2 py-1 rounded ${
                          qty < 10
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-green-100 text-green-800'
                        }`}
                      >
                        {qty.toLocaleString()}
                      </span>
                    </td>
                  </tr>
                );
              })
              )}
            </tbody>
          </table>
        </div>

        <div className="bg-gray-50 border-t px-6 py-4 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            <span className="font-medium">Selected:</span>{' '}
            <span className="font-semibold text-gray-900">{selectedBatch?.batchNo || 'None'}</span>
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              className="px-5 py-2 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition-colors"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              ref={selectButtonRef}
              type="button"
              className={`px-5 py-2 font-medium rounded-lg transition-colors ${
                selectedBatch && isExpired(selectedBatch.expiryDate)
                  ? 'bg-gray-400 text-white cursor-not-allowed'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700'
              }`}
              onClick={handleSelectBatch}
              disabled={!selectedBatch || (selectedBatch && isExpired(selectedBatch.expiryDate))}
            >
              Select
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '32px', // reduce overall control height
    height: '32px',    // explicitly set height
    backgroundColor: '#f9fafb',
    borderColor: state.isFocused ? '#6366f1' : '#b5b6b8ff',
    boxShadow: state.isFocused ? '0 0 0 1px #6366f1' : 'none',
    borderRadius: '0.5rem',
    '&:hover': {
      borderColor: '#6366f1',
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '2px 8px', // reduce internal padding for smaller height
    height: '32px',
  }),
  input: (provided) => ({
    ...provided,
    margin: '0px',
    padding: '0px',
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: '32px',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? '#92c3fcff'
      : state.isFocused
        ? '#92c3fcff'
        : '#ffffff',
    color: state.isSelected ? '#000000ff' : state.isFocused ? '#ffffff' : '#000000',
    fontWeight: '500',
    padding: '6px 8px',
    '&:hover': {
      backgroundColor: '#92c3fcff',
      color: '#000000ff',
    },
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 999,
    borderRadius: '0.5rem',
    overflow: 'hidden',
    boxShadow:
      '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
};


const customSelectStyles1 = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '37px', // reduce overall control height
    height: '37px',    // explicitly set height
    backgroundColor: '#f9fafb',
    borderColor: state.isFocused ? '#6366f1' : '#b5b6b8ff',
    boxShadow: state.isFocused ? '0 0 0 1px #6366f1' : 'none',
    borderRadius: '0.5rem',
    '&:hover': {
      borderColor: '#6366f1',
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '2px 8px', // reduce internal padding for smaller height
    height: '37px',
  }),
  input: (provided) => ({
    ...provided,
    margin: '0px',
    padding: '0px',
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: '37px',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? '#92c3fcff'
      : state.isFocused
        ? '#92c3fcff'
        : '#ffffff',
    color: state.isSelected ? '#000000ff' : state.isFocused ? '#ffffff' : '#000000',
    fontWeight: '500',
    padding: '8px 12px',
    '&:hover': {
      backgroundColor: '#92c3fcff',
      color: '#000000ff',
    },
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 999,
    borderRadius: '0.5rem',
    overflow: 'hidden',
    boxShadow:
      '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
};

// Normalize product data from API
const normalizeProductData = (items = []) =>
  items.map((item) => {
    const itemNo = item.Item_No ?? item.ItemNo ?? item.id ?? null;
    const itemName = item.Item_Name ?? item.ItemName ?? '';
    const packageValue = item.package ?? item.Package ?? '';
    const unitValue = item.unit ?? item.Unit ?? '';

    return {
      ...item,
      id: item.id ?? itemNo,
      Item_No: itemNo,
      Item_Name: itemName,
      ItemName: item.ItemName ?? itemName,
      package: packageValue,
      Package: item.Package ?? packageValue,
      unit: unitValue,
      Unit: item.Unit ?? unitValue,
      Company: item.Company ?? item.company ?? '',
      Chemical: item.Chemical ?? item.chemical ?? '',
      TaxCode: item.TaxCode ?? item.taxCode ?? '',
      Rate: item.Rate ?? item.rate ?? 0,
      MRP: item.MRP ?? item.mrp ?? 0,
    };
  });

const StockTransfer = () => {
  const token = localStorage.getItem('accessToken');

  const isElectron = () => window && window.process && window.process.type;

  let ipcRenderer;
  if (isElectron()) {
    ipcRenderer = window.require('electron').ipcRenderer;
  }

  const [loading, setLoading] = useState(false);
  const [transferNumber, setTransferNumber] = useState('');
  const [transferDate, setTransferDate] = useState('');
  const [originDepartment, setOriginDepartment] = useState(null);
  const [targetDepartment, setTargetDepartment] = useState(null);
  const [note, setNote] = useState('');
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [productsMaster, setProductsMaster] = useState([]);
  const [fetchingBatches, setFetchingBatches] = useState(false);
  const productSearchDebounceRef = useRef(null);

  // Items state - similar to Sales Entry
  const [products, setProducts] = useState([{ 
    product: null, 
    batch: null, 
    qty: 1, 
    id: null, 
    name: "",
    productData: null
  }]);

  // Batch selection modal state
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [selectedProductForBatch, setSelectedProductForBatch] = useState(null);
  const [currentProductIndex, setCurrentProductIndex] = useState(null);
  const [batchesForModal, setBatchesForModal] = useState([]);
  const [generalsettings, setGeneralSettings] = useState({});

  // Refs
  const targetDeptRef = useRef(null);
  const noteRef = useRef(null);
  const productRefs = useRef([]);
  const qtyRefs = useRef([]);
  const submitbtnRef = useRef(null);
  const isSelectingBatchRef = useRef(false);

  useEffect(() => {
    const fetchGeneralSettings = async () => {
      let generalSetting;
      if (isElectron() && ipcRenderer) {
        generalSetting = await ipcRenderer.invoke('store-get', 'generalopsettings');
      }
      setGeneralSettings(generalSetting || {});
    };
    fetchGeneralSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only; ipcRenderer from Electron
  }, []);

  // Load departments on mount
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await axios.get(`${BASE_URL}${GET_MASTER_DATA}`, {
          headers: {
            'XApiKey': process.env.REACT_APP_API_KEY,
            'Authorization': `Bearer ${token}`
          },
        });

        if (response.status === 200) {
          const deptData = (response.data.data || []).filter(item => item.Type === 'D');
          setDepartments(deptData);
        }
      } catch (error) {
        console.error('Failed to fetch departments:', error);
        toast.error('Could not load departments', {
          autoClose: 1500,
          icon: <FiAlertCircle className="text-red-500" />
        });
      }
    };

    if (token) {
      fetchDepartments();
    }
  }, [token]);

  // Origin department is always the logged-in user's department (localStorage)
  useEffect(() => {
    const savedId = localStorage.getItem('department_id');
    if (!savedId || departments.length === 0) {
      setOriginDepartment(null);
      return;
    }
    const match = departments.find((d) => String(d.ID ?? d.id) === String(savedId));
    if (!match) {
      setOriginDepartment(null);
      return;
    }
    setOriginDepartment({
      value: match.ID ?? match.id,
      label: match.Name ?? match.name,
    });
  }, [departments]);

  // Initial load - fetch first 1000 products
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${BASE_URL}${GET_ITEM_MASTER}`, {
          headers: {
            'XApiKey': process.env.REACT_APP_API_KEY,
            'Authorization': `Bearer ${token}`,
            'PageNo': 1,
            'PageSize': 1000,
            'FilterText': '',
            'FilterColumn': '0',
            'FilterValue': '0',
          },
        });

        if (response.status === 200) {
          const masterData = response.data?.data?.masterData || [];
          const pharmacyItems = masterData.filter(
            (item) => (item.ItemType ?? item.itemType) === 'Pharmacy'
          );
          setProductsMaster(normalizeProductData(pharmacyItems));
        }
      } catch (error) {
        console.error('Failed to fetch products:', error);
        toast.error('Could not load products', {
          autoClose: 1500,
          icon: <FiAlertCircle className="text-red-500" />
        });
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchProducts();
    }
  }, [token]);

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (productSearchDebounceRef.current) {
        clearTimeout(productSearchDebounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleWheel = (e) => {
      if (e.target.type === 'number') {
        e.target.blur();
      }
    };

    const handleKeyDown = (e) => {
      if (e.target.type === 'number') {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          e.preventDefault();
        }
      }
    };

    document.addEventListener('wheel', handleWheel, { capture: true });
    document.addEventListener('keydown', handleKeyDown, { capture: true });

    return () => {
      document.removeEventListener('wheel', handleWheel, { capture: true });
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, []);

  useEffect(() => {
    const now = new Date();
    const formattedDateTime = `${String(now.getDate()).padStart(2, '0')}-${String(now.getMonth() + 1).padStart(2, '0')}-${now.getFullYear()} ${now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}`;
    
    // Generate transfer number
    setTransferNumber(`ST${String(now.getFullYear()).slice(-2)}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}001`);
    
    // Set transfer date
    setTransferDate(formattedDateTime);
  }, []);

  // Load products from API based on search text (for AsyncSelect)
  const loadProductOptions = (inputValue, callback) => {
    // Clear previous debounce timeout
    if (productSearchDebounceRef.current) {
      clearTimeout(productSearchDebounceRef.current);
    }

    // If input is less than 2 characters, return initial products
    if (!inputValue || inputValue.length < 2) {
      const options = (productsMaster || []).map((product) => ({
        value: product.id,
        label: product.Item_Name ?? product.ItemName ?? '',
        genericName: product.Chemical ?? product.chemical ?? '',
        manufacturer: product.Company ?? product.company ?? '',
        ...product,
      }));
      callback(options);
      return;
    }

    // Debounce API call
    productSearchDebounceRef.current = setTimeout(async () => {
      try {
        const response = await axios.get(`${BASE_URL}${GET_ITEM_MASTER}`, {
          headers: {
            'XApiKey': process.env.REACT_APP_API_KEY,
            'Authorization': `Bearer ${token}`,
            'PageNo': 1,
            'PageSize': 1000,
            'FilterText': inputValue,
            'FilterColumn': '0',
            'FilterValue': '0',
          },
        });

        if (response.status === 200) {
          const masterData = response.data?.data?.masterData || [];
          const pharmacyItems = masterData.filter(
            (item) => (item.ItemType ?? item.itemType) === 'Pharmacy'
          );
          const normalizedProducts = normalizeProductData(pharmacyItems);
          
          const options = normalizedProducts.map((product) => ({
            value: product.id,
            label: product.Item_Name ?? product.ItemName ?? '',
            genericName: product.Chemical ?? product.chemical ?? '',
            manufacturer: product.Company ?? product.company ?? '',
            ...product,
          }));
          
          callback(options);
        } else {
          callback([]);
        }
      } catch (error) {
        console.error('Failed to fetch products:', error);
        callback([]);
      }
    }, 300); // 300ms debounce
  };

  // Get selected product option for AsyncSelect value prop
  const getSelectedProductOption = (productId) => {
    if (!productId) return null;
    const product = productsMaster.find(p => p.id === productId);
    if (!product) return null;
    return {
      value: product.id,
      label: product.Item_Name ?? product.ItemName ?? '',
      genericName: product.Chemical ?? product.chemical ?? '',
      manufacturer: product.Company ?? product.company ?? '',
      ...product,
    };
  };

  // Fetch batches for selected product
  const fetchBatchesForProduct = async (productId, product = null) => {
    setFetchingBatches(true);
    try {
      const response = await axios.get(`${BASE_URL}${GET_PRODUCT_BATCHES}`, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
        },
        params: {
          ItemNo: productId,
          DepartmentID: localStorage.getItem('department_id') || ''
        },
      });

      if (response.status === 200) {
        const batchData = response.data?.data || response.data || [];
        
        // Map batches to expected format; hide zero-qty batches
        const productBatches = batchData
          .filter(batch => String(batch.Item_No ?? batch.ItemNo ?? '') === String(productId))
          .map(batch => {
            const currentStock = parseFloat(batch.Onhand ?? batch.onhand ?? 0);
            const rate = batch.PurchasePrice ?? batch.purchasePrice ?? null;
            const mrp = batch.SalesPrice ?? batch.salesPrice ?? null;
            const tax = batch.TaxCode ?? batch.taxCode ?? null;
            
            return {
              batchNo: batch.BatchCode ?? batch.Batch_Code ?? batch.batchCode ?? '',
              expiryDate: batch.ExpiryDate ?? batch.Expiry_Date ?? batch.expiryDate ?? '',
              currentStock: currentStock,
              availableQty: currentStock,
              rate: rate !== null ? String(rate) : null,
              mrp: mrp !== null ? String(mrp) : null,
              tax: tax !== null ? parseFloat(tax) : null,
            };
          })
          .filter((batch) => batchAvailableQty(batch) > 0);

        return productBatches;
      } else {
        return [];
      }
    } catch (error) {
      console.error('Failed to fetch batches:', error);
      toast.error('Could not load batches for this product', {
        autoClose: 1500,
        icon: <FiAlertCircle className="text-red-500" />
      });
      return [];
    } finally {
      setFetchingBatches(false);
    }
  };

  // Handle product selection
  const handleProductChange = async (index, selectedOption) => {
    if (!selectedOption) {
      const updatedProducts = products.map((product, i) => 
        i === index ? { product: null, batch: null, qty: 1, id: null, name: "", productData: null } : product
      );
      setProducts(updatedProducts);
      return;
    }
    
    // Use selectedOption directly (it contains all product data from AsyncSelect)
    const selectedProduct = selectedOption;
    
    // Add to productsMaster if not already present
    if (selectedProduct && !productsMaster.find(p => p.id === selectedProduct.id)) {
      setProductsMaster(prev => [...prev, selectedProduct]);
    }
    
    if (selectedProduct) {
      // Fetch batches for the selected product
      const batches = await fetchBatchesForProduct(selectedProduct.Item_No ?? selectedProduct.id, selectedProduct);
      
      if (batches.length === 0) {
        toast.error('No batches available for this product', {
          autoClose: 1500,
          icon: <FiAlertCircle className="text-red-500" />
        });
        return;
      }
      
      // If product has multiple batches, show batch selection modal
      if (batches.length > 1) {
        setSelectedProductForBatch({
          ...selectedProduct,
          label: selectedProduct.Item_Name ?? selectedProduct.ItemName ?? '',
          batches: batches
        });
        setBatchesForModal(batches);
        setCurrentProductIndex(index);
        setShowBatchModal(true);
      } else if (batches.length === 1) {
        // If only one batch, select it automatically and focus quantity
        handleBatchSelection(index, selectedProduct, batches[0]);
        setTimeout(() => {
          if (qtyRefs.current[index]) {
            qtyRefs.current[index].focus();
            qtyRefs.current[index].select();
          }
        }, 100);
      }
    }
  };

  // Handle batch selection
  const handleBatchSelection = (index, product, selectedBatch) => {
    const qty = 1;
    const productName = product.Item_Name ?? product.ItemName ?? '';

    const newProducts = products.map((item, i) => {
      if (i === index) {
        return {
          id: product.id,
          product: { value: product.id, label: productName },
          batch: selectedBatch,
          qty: qty,
          name: productName,
          productData: product
        };
      }
      return item;
    });

    setProducts(newProducts);

    if (index === products.length - 1) {
      addProductRow();
    }
  };

  const addProductRow = () => {
    setProducts(prevProducts => [...prevProducts, { 
      product: null, 
      batch: null, 
      qty: 1, 
      id: null, 
      name: "",
      productData: null
    }]);

    // Expand refs arrays
    setTimeout(() => {
      const newLength = products.length + 1;
      if (productRefs.current.length < newLength) {
        productRefs.current = [...productRefs.current, null];
      }
      if (qtyRefs.current.length < newLength) {
        qtyRefs.current = [...qtyRefs.current, null];
      }
    }, 50);
  };

  const handleDeleteRow = (index) => {
    const newProducts = products.filter((_, i) => i !== index);
    setProducts(newProducts);
  };

  const getBatchKey = (product) => {
    if (!product?.id || !product?.batch) return null;
    const batchNo = String(product.batch.batchNo ?? '').trim();
    const expiry = String(product.batch.expiryDate ?? '').trim();
    return `${product.id}__${batchNo}__${expiry}`;
  };

  const getEnteredQtyForBatch = (items, batchKey, excludeIndex = -1) => {
    return items.reduce((total, item, idx) => {
      if (idx === excludeIndex) return total;
      if (!item?.batch || !item?.id) return total;
      if (getBatchKey(item) !== batchKey) return total;
      return total + (parseFloat(item.qty || 0) || 0);
    }, 0);
  };

  // Handle quantity changes with validation
  const handleQtyChange = (index, newQty) => {
    const updatedProducts = products.map((product, i) => {
      if (i === index && product.batch) {
        const parsedQty = parseFloat(newQty || 0);
        const qty = Number.isFinite(parsedQty) ? Math.max(parsedQty, 0) : 0;
        const availableStock = parseFloat(product.batch.currentStock || 0);
        const batchKey = getBatchKey(product);
        const alreadyEnteredQty = getEnteredQtyForBatch(products, batchKey, index);
        const remainingStock = Math.max(availableStock - alreadyEnteredQty, 0);
        
        // Validate quantity doesn't exceed remaining stock for same product+batch across rows
        if (qty > remainingStock) {
          toast.error(`Quantity cannot exceed remaining stock (${remainingStock}) for this batch.`, { autoClose: 2000 });
          return {
            ...product,
            qty: remainingStock
          };
        }
        
        return {
          ...product,
          qty: qty
        };
      }
      return product;
    });
    setProducts(updatedProducts);
  };

  // Handle quantity input key navigation
  const handleQtyKeyDown = (e, index) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Move to next product selection or submit button
      const nextIndex = index + 1;
      if (nextIndex < products.length && productRefs.current[nextIndex]) {
        productRefs.current[nextIndex].focus();
      } else {
        // If this is the last row, add a new row and focus it
        if (index === products.length - 1) {
          addProductRow();
          setTimeout(() => {
            if (productRefs.current[nextIndex]) {
              productRefs.current[nextIndex].focus();
            }
          }, 100);
        } else {
          // Focus submit button if no more rows
          submitbtnRef.current?.focus();
        }
      }
    }
  };

  const departmentOptions = departments.map(dept => ({
    value: dept.ID ?? dept.id,
    label: dept.Name ?? dept.name
  }));

  const targetDepartmentOptions = departmentOptions.filter(
    (dept) => String(dept.value) !== String(originDepartment?.value ?? '')
  );

  const customProductOptionLabel = ({ label, genericName, manufacturer }) => (
    <div className="flex flex-col">
      <span className="font-bold text-black text-sm">{label}</span>
      <span className="text-xs text-gray-600">
        Generic: {genericName} | {manufacturer}
      </span>
    </div>
  );

  const calculateTotalTransferQty = () => {
    return products
      .filter((product) => product.id && product.batch)
      .reduce((total, product) => total + (parseFloat(product.qty || 0) || 0), 0);
  };

  const resetForm = () => {
    const savedId = localStorage.getItem('department_id');
    let nextOrigin = null;
    if (savedId && departments.length > 0) {
      const match = departments.find((d) => String(d.ID ?? d.id) === String(savedId));
      if (match) {
        nextOrigin = {
          value: match.ID ?? match.id,
          label: match.Name ?? match.name,
        };
      }
    }
    setOriginDepartment(nextOrigin);
    setTargetDepartment(null);
    setProducts([{ product: null, batch: null, qty: 1, id: null, name: "", productData: null }]);
    setNote('');
    setTimeout(() => targetDeptRef.current?.focus(), 100);
  };

  const handleSave = async () => {
    setIsButtonDisabled(true);
    setLoading(true);

    if (!originDepartment) {
      toast.error('Your department could not be resolved. Please re-login or check department setup.', { autoClose: 2000 });
      setLoading(false);
      setIsButtonDisabled(false);
      return;
    }

    if (!targetDepartment) {
      toast.error('Please select target department.', { autoClose: 1000 });
      setLoading(false);
      setIsButtonDisabled(false);
      targetDeptRef.current?.focus();
      return;
    }

    if (originDepartment.value === targetDepartment.value) {
      toast.error('Origin and target departments cannot be the same.', { autoClose: 2000 });
      setLoading(false);
      setIsButtonDisabled(false);
      return;
    }
    
    const validProducts = products.filter(product => product.id && product.name && product.batch);
    
    if(validProducts.length === 0){
      toast.error('Please select at least one product with batch.', { autoClose: 1000 });
      setLoading(false);
      setIsButtonDisabled(false);
      if (productRefs.current[0]) {
        productRefs.current[0].focus();
      }
      return;
    }

    // Transfer qty is mandatory and must be greater than 0 for each item
    const zeroQtyIndex = products.findIndex(
      (product) =>
        product.id &&
        product.name &&
        product.batch &&
        parseFloat(product.qty || 0) <= 0
    );

    if (zeroQtyIndex !== -1) {
      const zeroQtyProduct = products[zeroQtyIndex];
      toast.error(
        `Transfer quantity is mandatory and must be greater than 0 for ${zeroQtyProduct.name || 'selected item'}.`,
        { autoClose: 2500 }
      );
      setLoading(false);
      setIsButtonDisabled(false);
      if (qtyRefs.current[zeroQtyIndex]) {
        qtyRefs.current[zeroQtyIndex].focus();
        qtyRefs.current[zeroQtyIndex].select();
      }
      return;
    }

    // Validate all quantities (row-level)
    const invalidProduct = validProducts.find(product => 
      parseFloat(product.qty || 0) > parseFloat(product.batch.currentStock || 0)
    );

    if (invalidProduct) {
      toast.error(`Quantity cannot exceed available stock for ${invalidProduct.name} - Batch ${invalidProduct.batch.batchNo}.`, { autoClose: 2000 });
      setLoading(false);
      setIsButtonDisabled(false);
      return;
    }

    // Validate combined quantities for duplicate product+batch rows
    const batchTotals = new Map();
    for (const product of validProducts) {
      const batchKey = getBatchKey(product);
      if (!batchKey) continue;

      const qty = parseFloat(product.qty || 0) || 0;
      const availableStock = parseFloat(product.batch?.currentStock || 0) || 0;

      const existing = batchTotals.get(batchKey) || {
        qty: 0,
        availableStock,
        productName: product.name,
        batchNo: product.batch?.batchNo
      };
      existing.qty += qty;
      batchTotals.set(batchKey, existing);
    }

    const exceededBatch = Array.from(batchTotals.values()).find(
      (entry) => entry.qty > entry.availableStock
    );

    if (exceededBatch) {
      toast.error(
        `Total quantity for ${exceededBatch.productName} - Batch ${exceededBatch.batchNo} cannot exceed ${exceededBatch.availableStock}.`,
        { autoClose: 2500 }
      );
      setLoading(false);
      setIsButtonDisabled(false);
      return;
    }

    // Prepare data according to API structure
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const transferDateValue = `${year}-${month}-${day}`; // Format: YYYY-MM-DD

    const items = validProducts.map((product, index) => {
      // Format expiry date properly (YYYY-MM-DD format)
      let formattedExpiryDate = '';
      if (product.batch?.expiryDate) {
        try {
          if (product.batch.expiryDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
            formattedExpiryDate = product.batch.expiryDate.substring(0, 10);
          } else {
            const date = new Date(product.batch.expiryDate);
            if (!isNaN(date.getTime())) {
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              formattedExpiryDate = `${year}-${month}-${day}`;
            }
          }
        } catch (error) {
          console.error('Error formatting expiry date:', error);
        }
      }

      const availableStock = parseFloat(product.batch?.currentStock || 0);
      const transferQty = parseFloat(product.qty || 0);
      const cost = product.batch?.rate ? parseFloat(product.batch.rate) : 0;
      const price = product.batch?.mrp ? parseFloat(product.batch.mrp) : 0;

      return {
        siNo: index + 1,
        itemNo: parseInt(product.productData?.Item_No ?? product.id) || 0,
        itemName: product.name || '',
        batchNo: product.batch?.batchNo || '',
        expiryDate: formattedExpiryDate,
        availableStock: availableStock,
        transferQty: transferQty,
        price: price,
        cost: cost
      };
    });

    const requestData = {
      transferDate: transferDateValue,
      note: note || '',
      originDepartmentID: originDepartment.value,
      targetDepartmentID: targetDepartment.value,
      sequence: 0,
      items: items
    };

    console.log('Stock Transfer Request Data:', requestData);

    try {
      const response = await axios.post(`${BASE_URL}${POST_STOCK_TRANSFER}`, requestData, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.status === 200 || response.status === 201) {
        toast.success('Stock Transfer Saved Successfully', { autoClose: 2000 });
        
        setTimeout(() => {
          resetForm();
          setLoading(false);
          setIsButtonDisabled(false);
        }, 2000);
      } else {
        throw new Error('Unexpected response status');
      }
    } catch (error) {
      console.error('Failed to save stock transfer:', error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to save stock transfer. Please try again.';
      toast.error(errorMessage, { autoClose: 3000 });
      setLoading(false);
      setIsButtonDisabled(false);
    }
  };

  return (
    <div className='flex h-full bg-background'>
      {/* <LeftMenu /> */}
      <ToastContainer
        position="top-center"
        autoClose={1000}
        limit={1}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Slide}
      />
          
      {loading && <Spinner />}

      <BatchSelectionModal
        isOpen={showBatchModal}
        onClose={() => {
          setShowBatchModal(false);
          if (
            !isSelectingBatchRef.current &&
            currentProductIndex !== null &&
            productRefs.current[currentProductIndex]
          ) {
            setTimeout(() => {
              productRefs.current[currentProductIndex].focus();
            }, 100);
          }
        }}
        product={selectedProductForBatch}
        batches={batchesForModal}
        generalsettings={generalsettings}
        onReopen={() => {
          if (document.activeElement && document.activeElement.tagName === 'INPUT') {
            document.activeElement.blur();
          }
          setShowBatchModal(true);
        }}
        onSelectBatch={(selectedBatch) => {
          isSelectingBatchRef.current = true;
          handleBatchSelection(currentProductIndex, selectedProductForBatch, selectedBatch);
          setShowBatchModal(false);
          setTimeout(() => {
            if (qtyRefs.current[currentProductIndex]) {
              qtyRefs.current[currentProductIndex].focus();
              qtyRefs.current[currentProductIndex].select();
            }
            isSelectingBatchRef.current = false;
          }, 150);
        }}
      />

      <div className='flex-1 flex h-full flex-col overflow-auto hide-scrollbar'>

        <div className="bg-white p-3 mb-3">
          <div>
            <h1 className="text-lg font-bold text-gray-800 flex items-center">
              Stock Transfer
            </h1>
          </div>
        </div>

        <div className="bg-white ml-5 mr-5 rounded-lg shadow-sm p-3 mb-3">
          <div className='w-full flex flex-row gap-3'>
            <div className='w-full flex p-3 bg-gray-50 rounded-lg border border-gray-200 flex-col gap-2.5'>
              {/* Transfer Info Section */}
              <div className="flex items-center gap-2 pb-2 border-b border-gray-300">
                <FiTruck className="text-indigo-600" size={16} />
                <h3 className="font-semibold text-gray-800 text-sm">Transfer Information</h3>
              </div>
              
              <div className='w-full'>
                <div className='grid grid-cols-12 gap-2.5'>

                  {/* Transfer Date */}
                  <div className='col-span-4'>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Transfer Date
                    </label>
                    <input
                      type="text"
                      className="w-full p-1.5 border border-gray-300 font-medium bg-gray-100 rounded-md text-xs"
                      value={transferDate}
                      readOnly
                    />
                  </div>

                  {/* Origin Department — fixed to logged-in department (localStorage department_id) */}
                  <div className='col-span-4'>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Origin Department <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      className="w-full p-1.5 border border-gray-300 font-medium bg-gray-100 rounded-md text-xs"
                      value={originDepartment?.label ?? ''}
                      readOnly
                      placeholder={
                        departments.length === 0
                          ? 'Loading…'
                          : originDepartment
                            ? ''
                            : 'No department for this login'
                      }
                    />
                  </div>

                  {/* Target Department */}
                  <div className='col-span-4'>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Target Department <span className="text-red-500">*</span>
                    </label>
                    <Select
                      options={targetDepartmentOptions}
                      className="w-full"
                      isSearchable
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      value={targetDepartment}
                      onChange={(selected) => {
                        setTargetDepartment(selected);
                        if (selected && productRefs.current[0]) {
                          setTimeout(() => productRefs.current[0].focus(), 100);
                        }
                      }}
                      onMenuClose={() => {
                        if (targetDepartment && productRefs.current[0]) {
                          setTimeout(() => productRefs.current[0].focus(), 100);
                        }
                      }}
                      isClearable
                      noOptionsMessage={() => "No departments found"}
                      styles={customSelectStyles}
                      ref={targetDeptRef}
                      isDisabled={!originDepartment}
                    />
                  </div>

                  {/* Note */}
                  <div className='col-span-12'>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Note
                    </label>
                    <input
                      type="text"
                      className="w-full p-1.5 border border-gray-300 font-medium bg-white rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-xs"
                      ref={noteRef}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          submitbtnRef.current?.focus();
                        }
                      }}
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="Enter reason for transfer..."
                    />
                  </div>
                  
                </div>
              </div>
            </div>
            
            {/* Summary Sidebar */}
            <div className='w-80 flex-shrink-0'>
              <div className="bg-gray-50 p-3 rounded-lg shadow-sm border border-gray-300 flex flex-col h-full">
                
                {originDepartment && (
                  <div className="bg-white p-2 rounded-md mb-2 border border-gray-300">
                    <div className="text-xs text-gray-600 mb-1">From:</div>
                    <div className="text-xs font-medium text-gray-700">
                      {originDepartment.label}
                    </div>
                  </div>
                )}

                {targetDepartment && (
                  <div className="bg-white p-2 rounded-md mb-2 border border-gray-300">
                    <div className="text-xs text-gray-600 mb-1">To:</div>
                    <div className="text-xs font-medium text-gray-700">
                      {targetDepartment.label}
                    </div>
                  </div>
                )}

                <div className="space-y-1.5 flex-grow text-xs">
                  <div className="flex justify-between items-center py-1.5 border-t border-b border-gray-400 my-1.5">
                    <span className="font-semibold text-gray-800">Total Items</span>
                    <span className="font-bold text-base text-indigo-600">
                      {products.filter(p => p.id).length}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-1.5 border-t border-b border-gray-400 my-1.5">
                    <span className="font-semibold text-gray-800">Total Transfer Qty</span>
                    <span className="font-bold text-base text-indigo-600">
                      {calculateTotalTransferQty()}
                    </span>
                  </div>
                </div>
                
                <div className="flex flex-col space-y-2 mt-3">
                  <div className="flex justify-between gap-2">                    
                    <button 
                      type="button" 
                      className="flex-1 py-2 px-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium text-xs rounded-md hover:from-indigo-700 hover:to-purple-700 shadow-sm transition-all"
                      onClick={handleSave} 
                      disabled={isButtonDisabled}
                      ref={submitbtnRef}
                    >
                      Transfer
                    </button>
                    <button 
                      type="button" 
                      className="flex-1 py-2 px-3 bg-btnblue text-white font-medium text-xs rounded-md hover:bg-indigo-700 shadow-sm transition-all"
                      onClick={resetForm}
                    >
                      New +
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Items Table */}
          <div className="mt-3">
            <div className="flex justify-between items-center mb-2 pb-1.5 border-b border-gray-300">
              <h3 className="font-semibold text-gray-800 text-sm">Items</h3>
            </div>
                      
            <div className="overflow-x-auto rounded-lg border border-gray-400">
              <table className="w-full border-collapse">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="p-3 text-left text-xs font-medium text-black uppercase tracking-wider">No.</th>
                    <th className="p-3 text-left text-xs font-medium text-black uppercase tracking-wider">Product</th>
                    <th className="p-3 text-left text-xs font-medium text-black uppercase tracking-wider">Batch No</th>
                    <th className="p-3 text-left text-xs font-medium text-black uppercase tracking-wider">Expiry Date</th>
                    <th className="p-3 text-right text-xs font-medium text-black uppercase tracking-wider">Available Stock</th>
                    <th className="p-3 text-left text-xs font-medium text-black uppercase tracking-wider">Transfer Qty</th>
                    <th className="p-3 text-center text-xs font-medium text-black uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                
                <tbody className="bg-white divide-y divide-gray-200">
                  {products.map((product, index) => (
                    <tr 
                      key={index} 
                      className="hover:bg-gray-50 transition-colors"
                    >                
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                        {index + 1}
                      </td>
                      
                      <td className="px-3 py-2 whitespace-nowrap text-sm" style={{ minWidth: '200px' }}>
                        <AsyncSelect
                          loadOptions={loadProductOptions}
                          className="w-full"
                          isSearchable
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          defaultOptions={true}
                          cacheOptions={true}
                          value={getSelectedProductOption(product.id)}
                          formatOptionLabel={customProductOptionLabel}
                          onChange={(selected) => handleProductChange(index, selected)}
                          onMenuClose={() => {
                            if (product.batch && qtyRefs.current[index]) {
                              setTimeout(() => {
                                qtyRefs.current[index].focus();
                                qtyRefs.current[index].select();
                              }, 100);
                            }
                          }}
                          isClearable
                          noOptionsMessage={({ inputValue }) => 
                            inputValue && inputValue.length < 2 
                              ? 'Type at least 2 characters to search' 
                              : 'No products found'
                          }
                          loadingMessage={() => 'Searching products...'}
                          styles={customSelectStyles1}
                          ref={(el) => (productRefs.current[index] = el)}
                          isDisabled={!originDepartment || !targetDepartment}
                        />
                      </td>

                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700 font-medium">
                        {product.batch ? product.batch.batchNo : '-'}
                      </td>

                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700">
                        {product.batch?.expiryDate ? (() => {
                          try {
                            const date = new Date(product.batch.expiryDate);
                            if (!isNaN(date.getTime())) {
                              return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
                            }
                            return product.batch.expiryDate;
                          } catch {
                            return product.batch.expiryDate;
                          }
                        })() : '-'}
                      </td>

                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700 font-medium text-right">
                        {product.batch ? product.batch.currentStock : '-'}
                      </td>

                      <td className="px-3 py-2 whitespace-nowrap text-sm" style={{ minWidth: '120px' }}>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 flex items-center pl-2 pointer-events-none text-indigo-600">
                            <FiArrowRight size={16} />
                          </div>
                          <input
                            type="number"
                            step="1"
                            min="1"
                            max={product.batch ? product.batch.currentStock : 0}
                            className={`w-full pl-8 pr-3 py-2 border ${
                              product.batch && (
                                parseFloat(product.qty || 0) <= 0 ||
                                parseFloat(product.qty || 0) > parseFloat(product.batch.currentStock || 0)
                              )
                                ? 'border-red-300 text-red-600' 
                                : 'border-indigo-300'
                            } text-black bg-white rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 transition-colors text-right font-medium`}
                            value={product.qty || ''}
                            onChange={(e) => handleQtyChange(index, e.target.value)}
                            onKeyDown={(e) => handleQtyKeyDown(e, index)}
                            onBlur={() => {
                              if (!product.batch) return;
                              if (parseFloat(product.qty || 0) <= 0) {
                                toast.error('Transfer quantity is mandatory and must be greater than 0.', {
                                  autoClose: 1500,
                                });
                              }
                            }}
                            ref={(el) => (qtyRefs.current[index] = el)}
                            placeholder="Qty"
                            title="Transfer quantity is mandatory and must be greater than 0"
                            onFocus={(e) => e.target.select()}
                            disabled={!product.batch}
                            required
                          />
                        </div>
                      </td>

                      <td className="px-3 py-2 whitespace-nowrap text-sm text-center">
                        {products.length > 1 && (
                          <button
                            onClick={() => handleDeleteRow(index)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                            title="Delete row"
                          >
                            <MdDelete size={18} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default withAuth(StockTransfer);
